import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  Person,
  Lock,
  Settings,
  Group,
  BarChart,
  Visibility,
  VisibilityOff,
  PersonAdd,
  CheckCircle,
  AccessTime,
  Search as SearchIcon,
  CalendarToday,
  Email,
  AdminPanelSettings,
  Block,
  LockOpen,
  Shield,
  WarningAmber,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { profileAPI } from '../services/api';
import { UserWithStats, UsageStats } from '../types';

// ─── Local preference keys ─────────────────────────────────────────────────────
const PREF_ROWS_KEY = 'kc:pref:recentSearchesRowsPerPage';
const PREF_THEME_KEY = 'kc:pref:theme';

interface ProfileSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onThemeChange: (mode: 'light' | 'dark') => void;
  currentTheme: 'light' | 'dark';
}

// ─── Tab Panel ─────────────────────────────────────────────────────────────────
const TabPanel: React.FC<{ children: React.ReactNode; value: number; index: number }> = ({
  children, value, index,
}) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

// ─── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; color: string }> = ({
  icon, label, value, color,
}) => (
  <Box sx={{
    p: 2,
    borderRadius: 2,
    border: `1px solid ${color}33`,
    backgroundColor: `${color}0D`,
    textAlign: 'center',
  }}>
    <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
    <Typography variant="h5" sx={{ fontWeight: 700, color: '#283E56' }}>{value}</Typography>
    <Typography variant="caption" sx={{ color: '#6B7280' }}>{label}</Typography>
  </Box>
);

// ─── Main Modal ────────────────────────────────────────────────────────────────

const ProfileSettingsModal: React.FC<ProfileSettingsModalProps> = ({
  open, onClose, onThemeChange, currentTheme,
}) => {
  const { user } = useAuth();

  const [tab, setTab] = useState(0);

  // ── Profile tab ──────────────────────────────────────────────────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ── Password tab ─────────────────────────────────────────────────────────────
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ── Preferences tab ──────────────────────────────────────────────────────────
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    parseInt(localStorage.getItem(PREF_ROWS_KEY) || '10', 10)
  );
  const [prefSaved, setPrefSaved] = useState(false);

  // ── User management tab ──────────────────────────────────────────────────────
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirst, setInviteFirst] = useState('');
  const [inviteLast, setInviteLast] = useState('');
  const [invitePw, setInvitePw] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMsg, setInviteMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deactivating, setDeactivating] = useState<{ [id: number]: boolean }>({});
  const [confirmTarget, setConfirmTarget] = useState<UserWithStats | null>(null);

  // ── Usage stats tab ──────────────────────────────────────────────────────────
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // ── Role-based access ─────────────────────────────────────────────────────────
  const userRole: string = (user as any)?.role || 'user';
  const canManageUsers = userRole === 'superadmin' || userRole === 'admin';
  const isSuperAdmin = userRole === 'superadmin';
  const STATS_TAB = canManageUsers ? 3 : 2;

  const getRoleChipStyle = (role: string) => {
    switch (role) {
      case 'superadmin': return { bg: '#EDE9FE', color: '#6D28D9', icon: <Shield sx={{ fontSize: '14px !important' }} /> };
      case 'admin': return { bg: '#EEF2F7', color: '#283E56', icon: <AdminPanelSettings sx={{ fontSize: '14px !important' }} /> };
      default: return { bg: '#F3F4F6', color: '#6B7280', icon: <Person sx={{ fontSize: '14px !important' }} /> };
    }
  };

  const canDeactivateUser = (target: UserWithStats): boolean => {
    if (target.id === user?.id) return false;
    if (isSuperAdmin) return target.role !== 'superadmin';
    if (userRole === 'admin') return target.role === 'user';
    return false;
  };

  // ── Seed form with user data on open ─────────────────────────────────────────
  useEffect(() => {
    if (open && user) {
      setFirstName(user.first_name);
      setLastName(user.last_name);
      setProfileMsg(null);
      setPwMsg(null);
      setInviteMsg(null);
    }
  }, [open, user]);

  // ── Load users when tab 2 is opened (admin/superadmin only) ──────────────────
  useEffect(() => {
    if (canManageUsers && tab === 2 && open) loadUsers();
  }, [tab, open, canManageUsers]);

  // ── Load stats when stats tab is opened ──────────────────────────────────────
  useEffect(() => {
    if (tab === STATS_TAB && open) loadStats();
  }, [tab, open, STATS_TAB]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await profileAPI.listUsers();
      setUsers(data);
    } catch { /* silent */ }
    finally { setUsersLoading(false); }
  };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const data = await profileAPI.getUsageStats();
      setStats(data);
    } catch { /* silent */ }
    finally { setStatsLoading(false); }
  };

  // ── Profile save ──────────────────────────────────────────────────────────────
  const handleSaveProfile = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setProfileMsg({ type: 'error', text: 'First and last name are required.' });
      return;
    }
    setProfileLoading(true);
    setProfileMsg(null);
    try {
      await profileAPI.updateProfile({ first_name: firstName.trim(), last_name: lastName.trim() });
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
      // update localStorage so the header reflects the change immediately
      const saved = localStorage.getItem('authUser');
      if (saved) {
        const parsed = JSON.parse(saved);
        parsed.first_name = firstName.trim();
        parsed.last_name = lastName.trim();
        localStorage.setItem('authUser', JSON.stringify(parsed));
      }
    } catch (e: any) {
      setProfileMsg({ type: 'error', text: e.message });
    } finally {
      setProfileLoading(false);
    }
  };

  // ── Password save ─────────────────────────────────────────────────────────────
  const handleChangePassword = async () => {
    if (!currentPw || !newPw || !confirmPw) {
      setPwMsg({ type: 'error', text: 'All password fields are required.' });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (newPw.length < 6) {
      setPwMsg({ type: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }
    setPwLoading(true);
    setPwMsg(null);
    try {
      await profileAPI.changePassword({ current_password: currentPw, new_password: newPw });
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (e: any) {
      setPwMsg({ type: 'error', text: e.message });
    } finally {
      setPwLoading(false);
    }
  };

  // ── Preferences save ──────────────────────────────────────────────────────────
  const handleSavePreferences = () => {
    localStorage.setItem(PREF_ROWS_KEY, String(rowsPerPage));
    window.dispatchEvent(new CustomEvent('kc:prefsUpdated', { detail: { rowsPerPage } }));
    setPrefSaved(true);
    setTimeout(() => setPrefSaved(false), 2500);
  };

  const handleThemeToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.checked ? 'dark' : 'light';
    localStorage.setItem(PREF_THEME_KEY, next);
    onThemeChange(next);
  };

  // ── Invite user ───────────────────────────────────────────────────────────────
  const handleInviteUser = async () => {
    if (!inviteEmail || !inviteFirst || !inviteLast || !invitePw) {
      setInviteMsg({ type: 'error', text: 'All invite fields are required.' });
      return;
    }
    setInviteLoading(true);
    setInviteMsg(null);
    try {
      await profileAPI.inviteUser({
        email: inviteEmail,
        first_name: inviteFirst,
        last_name: inviteLast,
        temp_password: invitePw,
        role: inviteRole,
      });
      setInviteMsg({ type: 'success', text: `${inviteFirst} ${inviteLast} has been added successfully as ${inviteRole}.` });
      setInviteEmail(''); setInviteFirst(''); setInviteLast(''); setInvitePw('');
      setInviteRole('user');
      loadUsers();
    } catch (e: any) {
      setInviteMsg({ type: 'error', text: e.message });
    } finally {
      setInviteLoading(false);
    }
  };

  // ── Toggle user active status ─────────────────────────────────────────────────
  // Step 1: show confirmation dialog
  const handleToggleStatus = (target: UserWithStats) => {
    setConfirmTarget(target);
  };

  // Step 2: user confirmed — execute the actual toggle
  const executeToggleStatus = async () => {
    if (!confirmTarget) return;
    const target = confirmTarget;
    setConfirmTarget(null);
    setDeactivating(prev => ({ ...prev, [target.id]: true }));
    try {
      await profileAPI.setUserActive(target.id, !target.is_active);
      setUsers(prev => prev.map(u => u.id === target.id ? { ...u, is_active: !target.is_active } : u));
    } catch (e: any) {
      setInviteMsg({ type: 'error', text: e.message });
    } finally {
      setDeactivating(prev => ({ ...prev, [target.id]: false }));
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const fmtDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const initials = [user?.first_name?.[0], user?.last_name?.[0]].filter(Boolean).join('').toUpperCase() || '?';

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: '90vh',
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        backgroundColor: '#283E56',
        color: 'white',
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 38, height: 38, backgroundColor: '#00C853', fontWeight: 700, fontSize: '0.9rem' }}>
            {initials}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>{user?.email}</Typography>
            {stats?.organization_name && (
              <Typography variant="caption" sx={{ opacity: 0.55, display: 'block', fontSize: '0.7rem' }}>
                {stats.organization_name}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <Close />
        </IconButton>
      </Box>

      {/* ── Tabs ── */}
      <Box sx={{ borderBottom: '1px solid #E5E7EB', backgroundColor: '#F8FAFC', flexShrink: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            '& .MuiTab-root': { minHeight: 48, fontWeight: 500, fontSize: '0.85rem', textTransform: 'none' },
            '& .Mui-selected': { color: '#283E56', fontWeight: 700 },
            '& .MuiTabs-indicator': { backgroundColor: '#283E56' },
          }}
        >
          <Tab icon={<Person sx={{ fontSize: 18 }} />} iconPosition="start" label="Profile" />
          <Tab icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" label="Preferences" />
          {canManageUsers && (
            <Tab icon={<Group sx={{ fontSize: 18 }} />} iconPosition="start" label="User Management" />
          )}
          <Tab icon={<BarChart sx={{ fontSize: 18 }} />} iconPosition="start" label="Usage Stats" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3, overflowY: 'auto' }}>

        {/* ══ TAB 0 — Profile ══════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Name section */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#283E56', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" /> Personal Information
              </Typography>

              {/* Read-only email */}
              <TextField
                fullWidth
                label="Email Address"
                value={user?.email || ''}
                disabled
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 18, color: '#9CA3AF' }} /></InputAdornment>,
                }}
                helperText="Email cannot be changed — it is your login identifier"
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth label="First Name" value={firstName} size="small"
                    onChange={e => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth label="Last Name" value={lastName} size="small"
                    onChange={e => setLastName(e.target.value)}
                  />
                </Grid>
              </Grid>

              {profileMsg && (
                <Alert severity={profileMsg.type} sx={{ mt: 2 }} onClose={() => setProfileMsg(null)}>
                  {profileMsg.text}
                </Alert>
              )}

              <Button
                variant="contained"
                onClick={handleSaveProfile}
                disabled={profileLoading}
                sx={{ mt: 2, backgroundColor: '#283E56', '&:hover': { backgroundColor: '#1C2433' } }}
                startIcon={profileLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {profileLoading ? 'Saving…' : 'Save Name'}
              </Button>
            </Box>

            <Divider />

            {/* Password section */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#283E56', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lock fontSize="small" /> Change Password
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth label="Current Password" type={showCurrentPw ? 'text' : 'password'}
                  value={currentPw} size="small"
                  onChange={e => setCurrentPw(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowCurrentPw(p => !p)}>
                          {showCurrentPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth label="New Password" type={showNewPw ? 'text' : 'password'}
                  value={newPw} size="small"
                  onChange={e => setNewPw(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowNewPw(p => !p)}>
                          {showNewPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth label="Confirm New Password" type="password"
                  value={confirmPw} size="small"
                  onChange={e => setConfirmPw(e.target.value)}
                  error={!!confirmPw && confirmPw !== newPw}
                  helperText={confirmPw && confirmPw !== newPw ? 'Passwords do not match' : ''}
                />
              </Box>

              {pwMsg && (
                <Alert severity={pwMsg.type} sx={{ mt: 2 }} onClose={() => setPwMsg(null)}>
                  {pwMsg.text}
                </Alert>
              )}

              <Button
                variant="contained"
                onClick={handleChangePassword}
                disabled={pwLoading}
                sx={{ mt: 2, backgroundColor: '#283E56', '&:hover': { backgroundColor: '#1C2433' } }}
                startIcon={pwLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {pwLoading ? 'Updating…' : 'Change Password'}
              </Button>
            </Box>
          </Box>
        </TabPanel>

        {/* ══ TAB 1 — Preferences ════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Results per page */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#283E56', mb: 1 }}>
                Recent Searches — Rows Per Page
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                Controls how many rows are shown in the Recent Searches table by default.
              </Typography>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Rows per page</InputLabel>
                <Select
                  value={rowsPerPage}
                  label="Rows per page"
                  onChange={e => setRowsPerPage(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map(n => (
                    <MenuItem key={n} value={n}>{n} rows</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            {/* Theme toggle */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#283E56', mb: 1 }}>
                Appearance
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                Switch between light and dark mode across the entire application.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentTheme === 'dark'}
                    onChange={handleThemeToggle}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#283E56' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#283E56' },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {currentTheme === 'dark' ? '🌙 Dark Mode' : '☀️ Light Mode'}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {prefSaved && (
              <Alert severity="success" icon={<CheckCircle fontSize="small" />}>
                Preferences saved.
              </Alert>
            )}

            <Box>
              <Button
                variant="contained"
                onClick={handleSavePreferences}
                sx={{ backgroundColor: '#283E56', '&:hover': { backgroundColor: '#1C2433' } }}
              >
                Save Preferences
              </Button>
            </Box>
          </Box>
        </TabPanel>

        {/* ══ TAB 2 — User Management ════════════════════════════════════════════ */}
        {canManageUsers && (
        <TabPanel value={tab} index={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Invite new user */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#283E56', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAdd fontSize="small" /> Add New User
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="First Name" value={inviteFirst} size="small"
                    onChange={e => setInviteFirst(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Last Name" value={inviteLast} size="small"
                    onChange={e => setInviteLast(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Email Address" value={inviteEmail} size="small" type="email"
                    onChange={e => setInviteEmail(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Temporary Password" value={invitePw} size="small" type="password"
                    onChange={e => setInvitePw(e.target.value)}
                    helperText="User should change this after first login" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={inviteRole}
                      label="Role"
                      onChange={e => setInviteRole(e.target.value)}
                    >
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {inviteMsg && (
                <Alert severity={inviteMsg.type} sx={{ mt: 2 }} onClose={() => setInviteMsg(null)}>
                  {inviteMsg.text}
                </Alert>
              )}

              <Button
                variant="contained"
                onClick={handleInviteUser}
                disabled={inviteLoading}
                sx={{ mt: 2, backgroundColor: '#283E56', '&:hover': { backgroundColor: '#1C2433' } }}
                startIcon={inviteLoading ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
              >
                {inviteLoading ? 'Adding…' : 'Add User'}
              </Button>
            </Box>

            <Divider />

            {/* Current users table */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#283E56', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Group fontSize="small" /> Team Members
              </Typography>

              {usersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} sx={{ color: '#283E56' }} />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#283E56' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#283E56' }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#283E56' }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#283E56' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#283E56' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map(u => (
                        <TableRow key={u.id} sx={{ '&:hover': { backgroundColor: 'rgba(40,62,86,0.03)' } }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', backgroundColor: u.id === user?.id ? '#00C853' : '#283E56' }}>
                                {(u.first_name[0] + u.last_name[0]).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {u.first_name} {u.last_name}
                                  {u.id === user?.id && (
                                    <Chip label="You" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.65rem', backgroundColor: '#dcfce7', color: '#166534' }} />
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#6B7280', fontSize: '0.82rem' }}>{u.email}</TableCell>
                          <TableCell>
                            {(() => {
                              const chipStyle = getRoleChipStyle(u.role);
                              return (
                                <Chip
                                  label={u.role === 'superadmin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : 'User'}
                                  size="small"
                                  icon={chipStyle.icon}
                                  sx={{ backgroundColor: chipStyle.bg, color: chipStyle.color, fontWeight: 600, fontSize: '0.72rem' }}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={u.is_active ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                backgroundColor: u.is_active ? '#dcfce7' : '#fef2f2',
                                color: u.is_active ? '#166534' : '#991b1b',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {canDeactivateUser(u) ? (
                              <Tooltip title={u.is_active ? 'Deactivate user' : 'Activate user'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!!deactivating[u.id]}
                                    onClick={() => handleToggleStatus(u)}
                                    sx={{
                                      color: u.is_active ? '#DC2626' : '#16A34A',
                                      '&:hover': { backgroundColor: u.is_active ? '#FEF2F2' : '#F0FDF4' },
                                    }}
                                  >
                                    {deactivating[u.id]
                                      ? <CircularProgress size={16} />
                                      : u.is_active ? <Block fontSize="small" /> : <LockOpen fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" sx={{ color: '#D1D5DB' }}>—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>
        </TabPanel>
        )}

        {/* ══ TAB 3 — Usage Stats ════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={STATS_TAB}>
          {statsLoading || !stats ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#283E56' }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* My activity */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#283E56', mb: 2 }}>
                  My Search Activity
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <StatCard
                      icon={<SearchIcon sx={{ fontSize: 28 }} />}
                      label="Total Searches"
                      value={stats.total_searches}
                      color="#283E56"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <StatCard
                      icon={<CalendarToday sx={{ fontSize: 28 }} />}
                      label="This Month"
                      value={stats.searches_this_month}
                      color="#3B82F6"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <StatCard
                      icon={<BarChart sx={{ fontSize: 28 }} />}
                      label="This Week"
                      value={stats.searches_this_week}
                      color="#10B981"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <StatCard
                      icon={<Group sx={{ fontSize: 28 }} />}
                      label="Total Users"
                      value={stats.total_users}
                      color="#F59E0B"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Account info */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#283E56', mb: 2 }}>
                  Account Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { icon: <CalendarToday fontSize="small" sx={{ color: '#6B7280' }} />, label: 'Member Since', value: fmtDate(stats.member_since) },
                    { icon: <AccessTime fontSize="small" sx={{ color: '#6B7280' }} />, label: 'Last Login', value: fmtDate(stats.last_login) },
                    { icon: <SearchIcon fontSize="small" sx={{ color: '#6B7280' }} />, label: 'Last Search', value: fmtDate(stats.last_search_date) },
                  ].map(row => (
                    <Box key={row.label} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: '#F8FAFC',
                      border: '1px solid #E5E7EB',
                    }}>
                      {row.icon}
                      <Typography variant="body2" sx={{ color: '#6B7280', minWidth: 120 }}>{row.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#283E56' }}>{row.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

            </Box>
          )}
        </TabPanel>

      </DialogContent>

      {/* ══ Confirmation Dialog ═══════════════════════════════════════════════ */}
      <Dialog
        open={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <WarningAmber sx={{ color: confirmTarget?.is_active ? '#DC2626' : '#16A34A', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#283E56' }}>
            {confirmTarget?.is_active ? 'Deactivate User' : 'Activate User'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: '4px !important' }}>
          <DialogContentText sx={{ color: '#374151', fontSize: '0.95rem' }}>
            {confirmTarget?.is_active ? (
              <>
                Are you sure you want to <strong>deactivate</strong>{' '}
                <strong>{confirmTarget?.first_name} {confirmTarget?.last_name}</strong>?{' '}
                They will no longer be able to log in.
              </>
            ) : (
              <>
                Are you sure you want to <strong>reactivate</strong>{' '}
                <strong>{confirmTarget?.first_name} {confirmTarget?.last_name}</strong>?{' '}
                They will regain access to the application.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmTarget(null)}
            variant="outlined"
            size="small"
            sx={{ borderColor: '#D1D5DB', color: '#6B7280', '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={executeToggleStatus}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: confirmTarget?.is_active ? '#DC2626' : '#16A34A',
              '&:hover': { backgroundColor: confirmTarget?.is_active ? '#B91C1C' : '#15803D' },
              fontWeight: 600,
            }}
          >
            {confirmTarget?.is_active ? 'Yes, Deactivate' : 'Yes, Activate'}
          </Button>
        </DialogActions>
      </Dialog>

    </Dialog>
  );
};

export default ProfileSettingsModal;
